import React from "react";
import {
  BurstPreset,
  ParticlePreset,
  ThemeConfig,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";

/**
 * A small canvas particle system that approximates what the buyer app draws
 * for each particle preset, plus the tap bursts. Not pixel-identical to the
 * app; it exists so admins can judge density, speed, size, wind and colours.
 */

type Kind =
  | "flake" | "drop" | "leaf" | "petal" | "rect" | "spark" | "star"
  | "ember" | "bubble" | "lantern" | "rocket" | "glyph" | "flash";

type P = {
  kind: Kind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rot: number;
  vr: number;
  phase: number;
  life: number; // seconds left; Infinity for ambient
  maxLife: number;
  gravity: number;
  drag: number;
  glyph?: string;
  burst?: boolean;
  targetY?: number;
};

const DEFAULT_COLORS: Record<ParticlePreset, string[]> = {
  none: [],
  snow: ["#FFFFFF"],
  rain: ["rgba(220,240,255,0.85)"],
  leaves: ["#E67E22", "#D35400", "#F1C40F", "#A04000"],
  petals: ["#FFB7C5", "#FF8FAB", "#FFD1DC"],
  sparkles: ["#FFFFFF", "#FFF3B0"],
  confetti: ["#FFD700", "#FF4D6D", "#4DD4FF", "#7CFF6B", "#FFFFFF"],
  fireworks: ["#FFD166", "#FF6B6B", "#7CF5FF", "#C77DFF", "#FFFFFF"],
  embers: ["#FF8C00", "#FFB703", "#FF5E00"],
  bubbles: ["rgba(255,255,255,0.85)"],
  lanterns: ["#FFB703", "#FF8C00", "#FF5E00"],
  stars: ["#FFFFFF", "#FFF3C4"],
};

/** Particles at density 1 on a 360 x 280 dash. */
const BASE_COUNT: Record<ParticlePreset, number> = {
  none: 0,
  snow: 90,
  rain: 120,
  leaves: 26,
  petals: 32,
  sparkles: 40,
  confetti: 70,
  fireworks: 0,
  embers: 50,
  bubbles: 28,
  lanterns: 12,
  stars: 60,
};

const GLYPH_PRESETS: ParticlePreset[] = [
  "snow", "leaves", "petals", "sparkles", "confetti", "embers", "bubbles", "lanterns", "stars",
];

const RISING: ParticlePreset[] = ["embers", "bubbles", "lanterns"];
const TWINKLE: ParticlePreset[] = ["sparkles", "stars"];

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

export type ParticleCanvasHandle = { burst: (x: number, y: number) => void };

type Props = {
  width: number;
  height: number;
  particles: ThemeConfig["particles"];
  burst: BurstPreset;
  /** Draw one settled frame and stop (mini previews, reduced motion). */
  still?: boolean;
};

export const ParticleCanvas = React.forwardRef<ParticleCanvasHandle, Props>(
  ({ width, height, particles, burst, still }, ref) => {
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const parts = React.useRef<P[]>([]);
    const rocketClock = React.useRef(0);
    const drawRef = React.useRef<(() => void) | null>(null);
    const loopRunning = React.useRef(false);
    const startLoopRef = React.useRef<(() => void) | null>(null);

    const cfg = particles;
    const preset = cfg.preset;
    const palette = cfg.colors.length ? cfg.colors : DEFAULT_COLORS[preset];
    const glyphs = GLYPH_PRESETS.includes(preset) ? cfg.glyphs : [];
    const cfgKey = JSON.stringify(cfg);

    const spawn = React.useCallback(
      (initial: boolean): P => {
        const s = cfg.size;
        const sp = cfg.speed;
        const wind = cfg.wind * 30 * sp;
        const color = pick(palette.length ? palette : ["#FFFFFF"]);
        const base: P = {
          kind: "flake",
          x: rand(-20, width + 20),
          y: initial ? rand(0, height) : -12,
          vx: wind,
          vy: 0,
          size: 2,
          color,
          rot: rand(0, Math.PI * 2),
          vr: 0,
          phase: rand(0, Math.PI * 2),
          life: Infinity,
          maxLife: Infinity,
          gravity: 0,
          drag: 0,
        };
        const glyph = glyphs.length ? pick(glyphs) : undefined;
        if (RISING.includes(preset)) base.y = initial ? rand(0, height) : height + 14;
        switch (preset) {
          case "snow":
            return { ...base, kind: glyph ? "glyph" : "flake", glyph, size: rand(1.4, 3.4) * s * (glyph ? 4 : 1), vy: rand(18, 34) * sp };
          case "rain":
            return { ...base, kind: "drop", size: rand(8, 14) * s, vx: cfg.wind * 160 * sp, vy: rand(320, 420) * sp };
          case "leaves":
            return { ...base, kind: glyph ? "glyph" : "leaf", glyph, size: rand(7, 11) * s * (glyph ? 1.4 : 1), vy: rand(28, 42) * sp, vr: rand(-1.5, 1.5) };
          case "petals":
            return { ...base, kind: glyph ? "glyph" : "petal", glyph, size: rand(5, 8) * s * (glyph ? 1.8 : 1), vy: rand(22, 36) * sp, vr: rand(-2, 2) };
          case "confetti":
            return { ...base, kind: glyph ? "glyph" : "rect", glyph, size: rand(3, 5) * s * (glyph ? 2.5 : 1), vy: rand(50, 75) * sp, vr: rand(-6, 6) };
          case "sparkles":
            return {
              ...base, kind: glyph ? "glyph" : "spark", glyph, x: rand(0, width), y: rand(0, height),
              vx: 0, size: rand(3, 6) * s * (glyph ? 2 : 1), life: rand(0.6, 2.2) / sp, maxLife: 2.2 / sp,
            };
          case "stars":
            return {
              ...base, kind: glyph ? "glyph" : "star", glyph, x: rand(0, width), y: rand(0, height),
              vx: cfg.wind * 4, size: rand(0.8, 2.2) * s * (glyph ? 5 : 1),
            };
          case "embers":
            return { ...base, kind: glyph ? "glyph" : "ember", glyph, size: rand(1.2, 2.6) * s * (glyph ? 4 : 1), vy: -rand(30, 55) * sp };
          case "bubbles":
            return { ...base, kind: glyph ? "glyph" : "bubble", glyph, size: rand(3, 8) * s * (glyph ? 2 : 1), vy: -rand(22, 40) * sp };
          case "lanterns":
            return { ...base, kind: glyph ? "glyph" : "lantern", glyph, size: rand(5, 8) * s * (glyph ? 2 : 1), vy: -rand(12, 22) * sp };
          default:
            return base;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [cfgKey, width, height],
    );

    const explode = React.useCallback(
      (x: number, y: number, colors: string[], n: number, power: number) => {
        for (let i = 0; i < n; i++) {
          const a = (Math.PI * 2 * i) / n + rand(-0.1, 0.1);
          const v = rand(0.5, 1) * power;
          parts.current.push({
            kind: "spark", x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v,
            size: rand(1.2, 2.2) * cfg.size, color: pick(colors), rot: 0, vr: 0, phase: 0,
            life: rand(0.8, 1.3), maxLife: 1.3, gravity: 70, drag: 1.4, burst: true,
          });
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [cfgKey],
    );

    const step = React.useCallback(
      (dt: number, t: number) => {
        const list = parts.current;
        // Fireworks: launch rockets on a clock.
        if (preset === "fireworks" && cfg.density > 0) {
          rocketClock.current -= dt;
          if (rocketClock.current <= 0) {
            rocketClock.current = rand(0.6, 1.6) / (0.3 + cfg.density * 1.4);
            list.push({
              kind: "rocket", x: rand(width * 0.1, width * 0.9), y: height + 4,
              vx: cfg.wind * 20, vy: -rand(200, 260) * Math.sqrt(cfg.speed),
              size: 1.6 * cfg.size, color: pick(palette), rot: 0, vr: 0, phase: 0,
              life: 4, maxLife: 4, gravity: 120 * cfg.speed, drag: 0,
              targetY: rand(height * 0.12, height * 0.5),
            });
          }
        }
        for (let i = list.length - 1; i >= 0; i--) {
          const p = list[i];
          p.life -= dt;
          p.vy += p.gravity * dt;
          if (p.drag) {
            p.vx -= p.vx * p.drag * dt;
            p.vy -= p.vy * p.drag * dt;
          }
          let sway = 0;
          if (p.kind === "flake" || p.kind === "leaf" || p.kind === "petal" || p.kind === "rect" || (p.kind === "glyph" && !p.burst)) {
            sway = Math.sin(t * 1.5 + p.phase) * (p.kind === "leaf" ? 26 : 14);
          } else if (p.kind === "bubble") {
            sway = Math.sin(t * 2 + p.phase) * 10;
          } else if (p.kind === "ember" || p.kind === "lantern") {
            sway = Math.sin(t * 1.2 + p.phase) * 8;
          }
          p.x += (p.vx + sway) * dt;
          p.y += p.vy * dt;
          p.rot += p.vr * dt;

          if (p.kind === "rocket" && (p.y <= (p.targetY ?? 0) || p.vy >= -30)) {
            list.splice(i, 1);
            explode(p.x, p.y, palette, Math.round(22 + 14 * cfg.size), 110 * Math.sqrt(cfg.speed));
            continue;
          }
          const out =
            p.y > height + 20 || p.y < -24 || p.x < -40 || p.x > width + 40 || p.life <= 0;
          if (out) {
            if (p.burst || p.kind === "rocket" || p.kind === "flash") {
              list.splice(i, 1);
            } else {
              // Twinkling presets respawn anywhere; the rest re-enter at the edge.
              list[i] = spawn(TWINKLE.includes(preset));
            }
          }
        }
      },
      [preset, cfg.density, cfg.speed, cfg.size, cfg.wind, width, height, palette, spawn, explode],
    );

    const draw = React.useCallback(
      (t: number) => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext("2d");
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
        for (const p of parts.current) {
          const fade = p.life !== Infinity ? Math.max(0, Math.min(1, p.life / (p.maxLife * 0.5))) : 1;
          ctx.globalAlpha = fade;
          ctx.fillStyle = p.color;
          ctx.strokeStyle = p.color;
          switch (p.kind) {
            case "flake":
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
              ctx.fill();
              break;
            case "drop":
              ctx.lineWidth = 1.2;
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(p.x - (p.vx / p.vy) * p.size, p.y - p.size);
              ctx.stroke();
              break;
            case "leaf":
            case "petal":
              ctx.save();
              ctx.translate(p.x, p.y);
              ctx.rotate(p.rot);
              ctx.beginPath();
              ctx.ellipse(0, 0, p.size, p.size * (p.kind === "leaf" ? 0.45 : 0.6), 0, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();
              break;
            case "rect":
              ctx.save();
              ctx.translate(p.x, p.y);
              ctx.rotate(p.rot);
              ctx.scale(1, Math.cos(t * 6 + p.phase));
              ctx.fillRect(-p.size, -p.size * 0.6, p.size * 2, p.size * 1.2);
              ctx.restore();
              break;
            case "spark": {
              const tw = p.burst ? 1 : Math.sin((1 - p.life / p.maxLife) * Math.PI);
              ctx.globalAlpha = fade * Math.max(0, tw);
              const r = p.size * (p.burst ? 1 : 1.6);
              ctx.beginPath();
              ctx.moveTo(p.x, p.y - r * 2);
              ctx.quadraticCurveTo(p.x, p.y, p.x + r * 2, p.y);
              ctx.quadraticCurveTo(p.x, p.y, p.x, p.y + r * 2);
              ctx.quadraticCurveTo(p.x, p.y, p.x - r * 2, p.y);
              ctx.quadraticCurveTo(p.x, p.y, p.x, p.y - r * 2);
              ctx.fill();
              break;
            }
            case "star":
              ctx.globalAlpha = 0.35 + 0.65 * Math.abs(Math.sin(t * 1.6 + p.phase));
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
              ctx.fill();
              break;
            case "ember":
              ctx.globalAlpha = 0.5 + 0.5 * Math.abs(Math.sin(t * 5 + p.phase));
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
              ctx.fill();
              break;
            case "bubble":
              ctx.lineWidth = 1;
              ctx.globalAlpha = 0.8;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
              ctx.stroke();
              ctx.globalAlpha = 0.5;
              ctx.beginPath();
              ctx.arc(p.x - p.size * 0.35, p.y - p.size * 0.35, p.size * 0.22, 0, Math.PI * 2);
              ctx.fill();
              break;
            case "lantern": {
              const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2.4);
              g.addColorStop(0, "rgba(255,220,120,0.55)");
              g.addColorStop(1, "rgba(255,220,120,0)");
              ctx.fillStyle = g;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size * 2.4, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = p.color;
              const w = p.size * 1.3;
              const h = p.size * 1.7;
              ctx.beginPath();
              ctx.moveTo(p.x - w / 2 + 2, p.y - h / 2);
              ctx.lineTo(p.x + w / 2 - 2, p.y - h / 2);
              ctx.lineTo(p.x + w / 2, p.y + h / 2);
              ctx.lineTo(p.x - w / 2, p.y + h / 2);
              ctx.closePath();
              ctx.fill();
              break;
            }
            case "rocket":
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
              ctx.fill();
              ctx.globalAlpha = 0.4;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(p.x - p.vx * 0.05, p.y - p.vy * 0.06);
              ctx.stroke();
              break;
            case "flash": {
              const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
              g.addColorStop(0, "rgba(255,255,230,0.9)");
              g.addColorStop(1, "rgba(255,255,230,0)");
              ctx.fillStyle = g;
              ctx.beginPath();
              ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
              ctx.fill();
              break;
            }
            case "glyph":
              ctx.save();
              ctx.translate(p.x, p.y);
              ctx.rotate(p.burst ? p.rot : Math.sin(t + p.phase) * 0.6 + p.rot * 0.2);
              ctx.font = `${Math.max(6, p.size)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(p.glyph ?? "*", 0, 0);
              ctx.restore();
              break;
          }
        }
        ctx.globalAlpha = 1;
      },
      [width, height],
    );

    // (Re)build the ambient particles whenever the config or size changes.
    React.useEffect(() => {
      const count = Math.round(BASE_COUNT[preset] * cfg.density * ((width * height) / (360 * 280)));
      parts.current = Array.from({ length: count }, () => spawn(true));
      rocketClock.current = 0.2;
      if (still) {
        // Settle a little so fireworks and twinkles have something to show.
        let t = 0;
        for (let i = 0; i < 60; i++) {
          step(1 / 30, t);
          t += 1 / 30;
        }
        draw(t);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfgKey, width, height, still]);

    // Animation loop. Sleeps when there is nothing to draw.
    React.useEffect(() => {
      if (still) {
        startLoopRef.current = null;
        return;
      }
      let raf = 0;
      let last = performance.now();
      let t = 0;
      let alive = true;
      const frame = (now: number) => {
        if (!alive) return;
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        t += dt;
        step(dt, t);
        draw(t);
        if (parts.current.length || preset === "fireworks") {
          raf = requestAnimationFrame(frame);
        } else {
          loopRunning.current = false;
        }
      };
      const start = () => {
        if (loopRunning.current) return;
        loopRunning.current = true;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      };
      startLoopRef.current = start;
      drawRef.current = () => draw(t);
      start();
      return () => {
        alive = false;
        loopRunning.current = false;
        cancelAnimationFrame(raf);
      };
    }, [still, step, draw, preset]);

    React.useImperativeHandle(
      ref,
      () => ({
        burst: (x: number, y: number) => {
          const add = (p: Partial<P>) =>
            parts.current.push({
              kind: "spark", x, y, vx: 0, vy: 0, size: 2, color: "#fff", rot: rand(0, 6), vr: rand(-4, 4),
              phase: rand(0, 6), life: 1.4, maxLife: 1.4, gravity: 0, drag: 1.2, burst: true, ...p,
            });
          const radial = (n: number, power: number, f: (a: number, v: number) => Partial<P>) => {
            for (let i = 0; i < n; i++) {
              const a = (Math.PI * 2 * i) / n + rand(-0.2, 0.2);
              const v = rand(0.45, 1) * power;
              add({ vx: Math.cos(a) * v, vy: Math.sin(a) * v, ...f(a, v) });
            }
          };
          const colors = cfg.colors.length ? cfg.colors : DEFAULT_COLORS.fireworks;
          switch (burst) {
            case "cracker":
              add({ kind: "flash", size: 34, life: 0.3, maxLife: 0.3, drag: 0 });
              radial(44, 170, () => ({ color: pick(colors), size: rand(1.2, 2.4), gravity: 90, life: rand(0.7, 1.2) }));
              break;
            case "confetti":
              radial(36, 160, () => ({
                kind: "rect", color: pick(DEFAULT_COLORS.confetti), size: rand(2.5, 4), gravity: 260,
                vy: -rand(80, 200), life: rand(1.2, 1.8), maxLife: 1.8,
              }));
              break;
            case "snow":
              radial(30, 80, () => ({ kind: "flake", color: "#FFFFFF", size: rand(1.4, 3), gravity: 25, life: rand(1.2, 1.8), maxLife: 1.8 }));
              break;
            case "hearts":
              radial(14, 110, () => ({ kind: "glyph", glyph: "❤️", size: rand(12, 18), gravity: -20, life: rand(1, 1.5) }));
              break;
            case "petals":
              radial(18, 110, () => ({ kind: "glyph", glyph: pick(["🌸", "🌼"]), size: rand(11, 16), gravity: 60, life: rand(1.1, 1.6), maxLife: 1.6 }));
              break;
          }
          if (still) {
            draw(0);
          } else {
            startLoopRef.current?.();
          }
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [burst, cfgKey, still, draw],
    );

    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    return (
      <canvas
        ref={canvasRef}
        width={Math.round(width * dpr)}
        height={Math.round(height * dpr)}
        style={{ position: "absolute", left: 0, top: 0, width, height, pointerEvents: "none" }}
      />
    );
  },
);

ParticleCanvas.displayName = "ParticleCanvas";

export default ParticleCanvas;
